import React, { useEffect } from 'react'
import { useParams } from 'react-router'
import { Button, Divider, Group, NumberInput, ScrollArea, Select, Stack, Table, Text, Title } from '@mantine/core'
import { useUpdateVersionRules, useVersionRulesCache } from '../../../../hooks/global-job-matching'
import { useRulesBasedPricingList, useRulesBasedPricingDataCuts, useUpdateRulesBasedPricingDataCut } from '../../../../hooks/rules-based-pricing'
import { DataCutPriority } from '../../GlobalJobMatchingVersioning/types/rules'
import { useAppState } from '../../../../hooks/redux'

export const DataCuts = () => {
  const params = useParams<{ id: string }>()
  const rules = useVersionRulesCache(params.id)
  const { data: rulesBasedPricingList } = useRulesBasedPricingList()
  const { groups, session: { organization: { defaultGroup, code: orgCode } } } = useAppState()
  const { mutate: updateRules } = useUpdateVersionRules(params.id)
  const { mutate: updateDataCut, isSuccess: dataCutUpdated, reset } = useUpdateRulesBasedPricingDataCut()
  const { data: dataCuts } = useRulesBasedPricingDataCuts({ dataCutPriority: rules?.dataCutPrioritySelection, enabled: !!rules?.dataCutPrioritySelection && rules?.dataCutPriority === 'rules_based_pricing' })

  const groupOptions = groups?.filter(group => group.id !== defaultGroup.id)?.map(group => ({ label: group.name, value: group.id.toString() }))
  const rulesBasePricingOptions = rulesBasedPricingList?.map(rule => ({ label: rule.name, value: rule.id.toString() }))

  const priorities = [
    { label: 'Site Level', value: 'default' },
    { label: 'Group Level', value: 'group_specific' },
    { label: 'Rules Based Pricing', value: 'rules_based_pricing', disabled: !rulesBasedPricingList?.length },
  ]

  const dataCutOptions = {
    default: [{ label: defaultGroup.name, value: defaultGroup.id.toString() }],
    group_specific: groupOptions || [],
    rules_based_pricing: rulesBasePricingOptions || []
  }

  if (dataCutUpdated) {
    updateRules({})
    reset()
  }

  useEffect(() => {
    if (!rules?.dataCutPrioritySelection) {
      updateRules({ dataCutPrioritySelection: dataCutOptions['default'][0]?.value})
    }
  }, [rules?.dataCutPrioritySelection])

  const rows = dataCuts?.map((cut) => (
    <Table.Tr key={cut.dataCut.id}>
      <Table.Td pl={24}>
        <Stack gap="xs">
          <Text fw={700}>{cut.dataCut.name}</Text>
          <Text size="sm">{cut.dataCut.survey.surveyShortName || cut.dataCut.survey.surveyLongName}</Text>
          <Text size="sm">{cut.dataCut.survey.surveyCode}</Text>
        </Stack>
      </Table.Td>
      <Table.Td>{cut.dataCut.currency.code}</Table.Td>
      <Table.Td pr={24}>
        <NumberInput
          max={10}
          min={1}
          onChange={(priority) => updateDataCut({ dataCutPriority: rules.dataCutPrioritySelection, priorityValue: priority, dataCutConnectionId: cut.id })}
          value={cut.priority}
        />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack p='xl' h='calc(100vh - 300px)' style={{ overflow: 'scroll' }}>
      <Title order={2} style={{ fontWeight: 600 }}>Select Data Cut Priorities</Title>
      <Text  size='lg'>Define and configure your Survey Data cut priorities</Text>
      <Divider size='sm' my='lg' />
      <Stack mb='xl'>
        <Select
          label='Source'
          data={priorities}
          value={rules?.dataCutPriority}
          size='lg'
          allowDeselect={false}
          onChange={(val: DataCutPriority) => updateRules({ dataCutPriority: val, dataCutPrioritySelection: dataCutOptions[val]?.length ? dataCutOptions[val][0]?.value : '' })}
        />
        <Select
          label='Priority'
          data={dataCutOptions[rules?.dataCutPriority]}
          value={rules?.dataCutPrioritySelection}
          allowDeselect={false}
          size='lg'
          onChange={(val: string) => updateRules({ dataCutPrioritySelection: val })}
        />
      </Stack>
        { rules?.dataCutPriority === 'default' &&
        <Group justify='space-between' mb='xl'>
          <Stack gap='xs'>
            <Title>Do you need to update or set data cut priorities?</Title>
            <Text  size='lg'>Browse survey library will open in a separate tab</Text>
          </Stack>
          <Button size='lg' color='hsl(214, 35%, 15%)' onClick={() => window.open(`/view/o/${orgCode}/mp/iframe/merit/merit-admin/survey_lib`, '_blank')}>
            View data cuts
          </Button>
        </Group>
        }
        <Stack>
          {rules?.dataCutPriority === 'rules_based_pricing' &&
            <Group justify='space-between' mb='xl'>
              <Stack gap='xs'>
                <Title>Do you need to update or set data cut priorities?</Title>
                <Text>Rules based pricing will open in a separate tab</Text>
              </Stack>

              <Button size='lg' color='hsl(214, 35%, 15%)' w='400' onClick={() => window.open(`/view/o/${orgCode}/mp/market-analysis/rules-based-pricing/rules/${rules?.dataCutPrioritySelection}`, '_blank')}>
                View Rules Based Pricing
              </Button>
            </Group>
          }
          {rules?.dataCutPriority === 'rules_based_pricing' && !!rules.dataCutPrioritySelection && (
            dataCuts?.length !== 0 ? (
              <ScrollArea>
                <Table stickyHeader h='auto'>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th pl={24}>Data Cut</Table.Th>
                      <Table.Th>Currency</Table.Th>
                      <Table.Th pr={24} w={50}>Priority</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            ) : (
             <Text  size='lg'>There are no selected data cuts associated with this rule.</Text>
            )
          )}
        </Stack>
    </Stack>
  )
}
